import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CircleDollarSign, Plus, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import {
  invalidatePricingList,
  ruleCountOf,
  usePricingListsQuery,
  type PricingListRow,
} from '../pricingListsApi';
import { pricingListRoute } from '../pricingRoutes';
import { PricingListFormDialog } from './PricingListFormDialog';

/**
 * "Which rate card does this charge from?" — on the resource form, in both the
 * places the venue owner named: editing a resource, and adding one.
 *
 * ## The empty select is the whole point of this component
 *
 * Someone adding their first PlayStation has no rate cards. A select with one
 * option that says "none" is a dead end, and it is precisely the dead end this
 * change exists to remove: they would save the console, walk to it, press Start,
 * and be told it has no price — which is where they were before any of this.
 *
 * So the control carries a way out that does not cost them the form they are
 * halfway through filling in. **Creating a card happens here**, in a small
 * dialog over this one, and the new card comes back already chosen. Navigating
 * to the card editor instead would have been simpler to write and would have
 * thrown away a half-typed resource — the failure would have moved, not gone.
 *
 * The route to the full editor is offered as well, because the card that was
 * just created has no *rates* on it yet, and a manager who does not go and add
 * one will meet the second refusal instead of the first. The notice below says
 * so rather than letting them find out at the console.
 */
export function PricingListSelect({
  value,
  onChange,
  onNavigateAway,
}: {
  /** The chosen card's id, or `''` for none. */
  value: string;
  onChange: (listId: string) => void;
  /**
   * Called before this control routes away from the screen it sits on.
   *
   * The resource form is a dialog: leaving it up over a different route would
   * strand the manager behind a form for a resource they can no longer see.
   */
  onNavigateAway?: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [creating, setCreating] = useState(false);

  const canRead = can('resources.view');
  const canCreate = can('resources.create');
  const query = usePricingListsQuery(undefined, { skip: !canRead });

  /**
   * Only the cards a resource may actually be attached to.
   *
   * The API refuses a deactivated one with `RESOURCE_PRICING_LIST_DEACTIVATED`,
   * so offering it would be offering a 409 — and the retired card would sit in
   * the picker looking exactly like the live one beside it.
   */
  const lists = useMemo(
    () => (query.data ?? []).filter((list) => list.isActive),
    [query.data],
  );

  const chosen = lists.find((list) => list.id === value) ?? null;
  // A card with no rates is attachable and still cannot price a session — the
  // second of the three refusals, met at the console instead of here.
  const chosenIsEmpty = chosen !== null && ruleCountOf(chosen) === 0;

  const leaveFor = (listId?: string | null) => {
    onNavigateAway?.();
    navigate(pricingListRoute(listId));
  };

  const adopt = (list: PricingListRow) => {
    // The index carries the counts this control reads, and a card created here
    // is not in it yet: without this the new card would be chosen and the
    // "no rates yet" notice under it would never appear.
    dispatch(invalidatePricingList(list.id));
    onChange(list.id);
  };

  return (
    <>
      <Field
        label={t('spaces.pricingRules.lists.attach')}
        hint={t('spaces.pricingRules.lists.attachHint')}
      >
        <Select
          value={value}
          disabled={!canRead || query.isLoading}
          onChange={(event) => onChange(event.currentTarget.value)}
          className="h-12"
        >
          <option value="">{t('spaces.pricingRules.lists.none')}</option>
          {lists.map((list) => (
            <option key={list.id} value={list.id}>
              {list.name}
            </option>
          ))}
        </Select>
      </Field>

      <div className="space-y-2">
        {!query.isLoading && lists.length === 0 && (
          <p className="text-xs text-ink-muted" dir="auto">
            {t('spaces.pricingRules.lists.noneYet')}
          </p>
        )}

        {chosenIsEmpty && (
          <p
            className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300"
            dir="auto"
          >
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('spaces.pricingRules.lists.chosenHasNoRules', { name: chosen.name })}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              {t('spaces.pricingRules.lists.createInline')}
            </Button>
          )}
          {canRead && (
            <Button size="sm" variant="ghost" onClick={() => leaveFor(value || null)}>
              <CircleDollarSign className="h-4 w-4" />
              {t('spaces.pricingRules.manage')}
            </Button>
          )}
        </div>
      </div>

      {/* Over the resource form rather than instead of it: the half-typed
          console is still there when this closes, with the new card chosen. */}
      <PricingListFormDialog
        open={creating}
        record={null}
        onClose={() => setCreating(false)}
        onSaved={adopt}
      />
    </>
  );
}
